import type { SelectedProduct } from "../types/campaign";
import { CampaignType } from '../types/campaign';
import { ContentRequirement } from '../types/content';
import { FoodfluenceLocation } from '../types/foodfluenceLocation';
import { InfluencerCancelBlockedReason } from '../utils/collabActionGuards';

/** A go-live window as the creator saw it: 'YYYY-MM-DD' days, start = end for a specific date. */
export interface GoLiveWindowDto {
  start: string;
  end: string;
}

export interface CreateCollabRequestDto {
    influencerID: string;
    deliverableCounts: Record<string, number>;
    cash: number;
    credit: number;
    campaignId: string | null;
    note?: string | null;
    customCollabType: CampaignType;
    clientId: string;
    isExternalClient: boolean;
    clientName: string;
    clientEmail: string;
    influencerHandle: string;
    prospectiveEmails: string[];
    proposedSelectedLocationIds?: string[];
    /** UGC custom proposals: the creator's proposed content deliverables (Collab.proposedContent) */
    proposedContent?: Record<string, ContentRequirement>;
    /**
     * Signed token proving the creator reached the brand via its shared Creator Link
     * (minted by the dashboard into the link's ?inbound= param, verified before
     * tagging/auto-approving).
     */
    inboundLinkToken?: string;
    /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
    goLiveConfirmed?: GoLiveWindowDto;
}

export interface UpdateCollabRequestDto {
  collabId: string;
  deliverableCounts: Record<string, number>;
  cash: number;
  credit: number;
  note?: string;
  customCollabType?: CampaignType;
  proposedSelectedLocationIds?: string[];
  proposedContent?: Record<string, ContentRequirement>;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
}

export interface CollabResponseDto {
  success: boolean;
  collabId: string;
} 


export interface AcceptShopifyCollabRequestDto {
  collabId: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  variantIds: string[];
  /**
   * What the creator chose, so the accept can RECORD it and not merely ship it. `variantIds` is
   * what the draft order needs; this is what every read surface and the charge read afterwards.
   */
  selectedProducts?: SelectedProduct[];
  /** Browser IANA timezone; stored on the creator's profile as the due-date fallback for types with no store. */
  timezone?: string;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
}

export interface AcceptShopifyCollabResponseDto {
  success: boolean;
  orderId: string;
  message?: string;
} 

export interface GetLocationForCollabResponseDto {
  success: boolean;
  data: FoodfluenceLocation;
  error?: string;
}

export interface AdvanceCollabToContentSubmissionDto {
  collabId: string;
  skipRedeem?: boolean;
  requireDraftApproval?: boolean;
  /**
   * A retail collab the brand accepted first reaches `pendingRedemption`, and the creator makes
   * their product choice on the way through this path rather than through an accept endpoint.
   * Without it the choice would never be persisted and the collab would bill on the campaign's
   * estimate.
   */
  selectedProducts?: SelectedProduct[];
}

export interface AcceptShipToHomeCollabDto {
  collabId: string;
  shippingAddress: string;
  country?: string;
  state?: string;
  requireDraftApproval?: boolean;
  /** What the creator chose, when the campaign offers selection groups. Validated server-side. */
  selectedProducts?: SelectedProduct[];
  /** Browser IANA timezone; stored on the creator's profile as the due-date fallback for types with no store. */
  timezone?: string;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
}

export interface AcceptRetailCollabDto {
  collabId: string;
  selectedLocation: string;
  postingDate: string;
  selectedProducts?: SelectedProduct[];
  /** Browser IANA timezone; stored on the creator's profile as the due-date fallback for types with no store. */
  timezone?: string;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
}

export interface AcceptBrandManagedCollabDto {
  collabId: string;
  selectedProducts?: SelectedProduct[];
  /** Browser IANA timezone; stored on the creator's profile as the due-date fallback for types with no store. */
  timezone?: string;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
}

export interface GetAvailableSpotsRequestDto {
  campaignId: string;
}

export interface GetAvailableSpotsResponseDto {
  success: boolean;
  availableSpots: number;
  takenCount: number;
  maxCollabs: number | null;
}

export interface AvailableScheduleSlotDto {
  time: string;
  timezone: string;
  /** Shaping layer: the single pre-selected recommended slot. */
  recommended?: boolean;
  /** Shaping layer: spots left in this slot's week when a weekly cap exists. */
  weekRemaining?: number;
}

export interface GetAvailableScheduleSlotsResponseDto {
  success: boolean;
  slots: Record<string, AvailableScheduleSlotDto[]>;
  hasBookableSlots: boolean;
  /** True when the curated response omitted legal slots reachable via ?expanded=true. */
  hasMoreSlots?: boolean;
}

export interface ValidateScheduleSlotRequestDto {
  campaignId: string;
  collabId: string;
  scheduledCollabDate: string;
  locationId?: string;
  /** Browser IANA timezone; used when no store location timezone is available */
  timezone?: string;
}

export interface ValidateScheduleSlotResponseDto {
  success: boolean;
  valid: boolean;
  reason?: string;
}

export interface RecordCollabViewedTimeDto {
  collabId: string;
}

export type InStoreCheckInKind = 'preVisit' | 'postVisit';

/**
 * Set (or reschedule) an in-store collab's visit date server-side. The backend writes
 * the date + schedule log and (re)schedules the pre-visit check-in atomically, so the
 * GCP task can't be left stale by a failed client call.
 */
export interface ScheduleInStoreVisitDto {
  collabId: string;
  /** ISO string for the selected visit date/time. */
  scheduledDate: string;
  selectedLocation?: string;
  partySize?: number;
  /** True when accepting the collab for the first time; false when rescheduling an accepted collab. */
  isAccept: boolean;
  /** Browser IANA timezone, used as a fallback when the store location has no timezone. */
  timezone?: string;
  /** The go-live window the creator saw and confirmed; required server-side on go-live campaigns. */
  goLiveConfirmed?: GoLiveWindowDto;
  /**
   * Creator's chosen turnaround in days (visit → post). Clamped server-side to
   * DEFAULT_POST_TURNAROUND_DAYS. Omit on a reschedule to keep the existing turnaround — the
   * server derives it from the collab and moves the posting date with the new visit.
   */
  turnaround?: number;
}

/**
 * Why a proposed reschedule date/time isn't (yet) bookable. Computed authoritatively by the
 * web backend (which owns the campaign window, open hours, capacity and lead-time floor) so the
 * dashboard agent can give the creator a specific explanation instead of a generic "not available".
 *
 * Date-level dead-ends (no openings that whole day, for a specific reason):
 *  - after_campaign_end   proposed day is past the campaign/collab end date (see campaignEndLocalDate)
 *  - before_campaign_start proposed day is before the campaign start (see campaignStartLocalDate)
 *  - too_soon             the whole day falls within the scheduling lead-time floor (e.g. <24h out)
 *  - day_closed           the store has no open hours on that weekday
 *  - day_unavailable      the day is otherwise open/in-window but has no bookable openings left (full)
 * Time-level:
 *  - time_unavailable     the day HAS openings, but the specific requested time isn't one of them
 *  - needs_time           the day HAS openings and no time was given yet — ask which time (see daySlots)
 */
export type RescheduleUnavailableReason =
  | 'after_campaign_end'
  | 'before_campaign_start'
  | 'too_soon'
  | 'day_closed'
  | 'day_unavailable'
  | 'time_unavailable'
  | 'needs_time';

/**
 * Internal, service-to-service request from the dashboard inbox rescheduling agent.
 * Authenticated with the shared CLIENT_BACKEND_KEY (see internalServiceAuth middleware),
 * NOT influencer auth. The web backend owns the tz math + validation + non-burning write.
 */
/**
 * Internal (service-authed) cancel driven by the dashboard inbox cancel agent.
 *
 * Two-phase like AgentRescheduleRequestDto: the agent calls with `commit: false` to ask whether
 * this collab may be canceled at all (which decides whether it even offers the creator the
 * confirm buttons), then with `commit: true` once they tap "Yes, cancel".
 */
export interface AgentCancelRequestDto {
  collabId: string;
  /** Stored on the collab as `influencerCancelReason`. Ignored when `commit` is false. */
  reason?: string;
  commit: boolean;
}

export interface AgentCancelResponseDto {
  success: boolean;
  /** True when the creator is currently allowed to cancel this collab. */
  eligible: boolean;
  /** Machine-readable refusal (mirrors InfluencerCancelBlockedReason) when not eligible. */
  blockedReason?: InfluencerCancelBlockedReason;
  /** Whether a cancellation was actually committed on this call. */
  canceled: boolean;
}

/**
 * Internal (service-authed) "the creator received their Shopify gifting order", driven by the
 * dashboard's Shopify arrival check-in: the creator tapped "Yes, got it!", or never answered and
 * the carrier confirmed delivery. Performs the same transition as the app's "I've Received My
 * Order" button, and only while the collab is still awaiting delivery.
 */
export interface MarkShopifyOrderReceivedRequestDto {
  collabId: string;
}

export interface MarkShopifyOrderReceivedResponseDto {
  success: boolean;
  /** True when the collab is now past "awaiting delivery", including when it already was. */
  received: boolean;
  alreadyReceived?: boolean;
  /** Why it was not marked received. */
  reason?: 'notShopify' | 'notAwaitingDelivery';
}

/**
 * Internal (service-authed) "the creator received their ship-to-home product", driven by the
 * dashboard's arrival check-in chips. Same effect as the app's "I received my product" button,
 * acting as the collab's own creator: records productReceivedTime and starts the content clock
 * unless shipping confirmation already did (both set-once, so a replay writes nothing).
 */
export interface MarkShipToHomeReceivedRequestDto {
  collabId: string;
}

export interface MarkShipToHomeReceivedResponseDto {
  /** True when the creator's receipt is on file, including when it already was (reason 'alreadyReceived'). */
  received: boolean;
  reason?: 'notFound' | 'notShipToHome' | 'notAwaitingReceipt' | 'alreadyReceived';
  /** ISO content due date after this call (null for legacy collabs with no clock). Only when newly received. */
  contentDueDate?: string | null;
}

export interface AgentRescheduleRequestDto {
  collabId: string;
  /** Local date 'YYYY-MM-DD' in the store timezone. Omit (with proposedTimeLabel) to just fetch bookable slots. */
  proposedLocalDate?: string;
  /** Time label like '2:00 PM'. Omit (with proposedLocalDate) to just fetch bookable slots. */
  proposedTimeLabel?: string;
  /** When true AND the proposed time is valid, execute the reschedule (logged as 'agent_reschedule', non-burning). */
  commit: boolean;
}

export interface AgentRescheduleResponseDto {
  success: boolean;
  /** Whether a concrete proposed date+time was supplied and evaluated. */
  hasProposedTime: boolean;
  /** True when the proposed time is bookable (open hours, capacity, campaign window, >=24h out). */
  valid: boolean;
  /** Human-readable reason when not valid (or not eligible). */
  reason?: string;
  /** The resolved ISO datetime for the proposed local date/time (when hasProposedTime). */
  resolvedDateIso?: string;
  /** Whether a reschedule was actually committed on this call. */
  rescheduled: boolean;
  /** IANA timezone used for evaluation. */
  timezone?: string;
  /** Bookable slots keyed by UTC-midnight ISO day — offer these when the proposal is invalid. */
  availableSlots?: Record<string, AvailableScheduleSlotDto[]>;
  /**
   * Whether the creator still has a self-serve reschedule left (MAX_INFLUENCER_RESCHEDULES).
   *
   * Reported rather than enforced, because the two callers want opposite things. The rescheduling
   * AGENT deliberately ignores it — an agent reschedule is logged as 'agent_reschedule' and burns
   * nothing, which is why it engages even when the creator's own allowance is gone. The cancel
   * agent's Reschedule chip must respect it, because that chip leads to the app's scheduling
   * screen, which does burn one and would turn them away at the door.
   */
  selfServeRescheduleAvailable?: boolean;
  /**
   * Machine-readable reason the proposed date/time isn't (yet) bookable, so the agent can give a
   * specific reply. Set alongside `valid: false` on the proposal-evaluation path.
   */
  reasonCode?: RescheduleUnavailableReason;
  /** Store-local 'YYYY-MM-DD' campaign end date — set when reasonCode is 'after_campaign_end'. */
  campaignEndLocalDate?: string;
  /** Store-local 'YYYY-MM-DD' campaign start date — set when reasonCode is 'before_campaign_start'. */
  campaignStartLocalDate?: string;
  /**
   * Open time labels (e.g. ['2:00 PM','3:00 PM']) for the specific proposed date — set when that
   * date is bookable but the time is missing ('needs_time') or off-slot ('time_unavailable'), so
   * the agent can offer that day's times directly.
   */
  daySlots?: string[];
}

/**
 * Creator ends a collab they had already accepted. `reason` is their free-text or picked
 * reason, surfaced to the brand in the thread and stored on the collab.
 */
export interface CancelCollabRequestDto {
  collabId: string;
  reason?: string;
}

/** Creator turns down an invite they never accepted. Same reason handling as cancel. */
export interface DeclineCollabRequestDto {
  collabId: string;
  reason?: string;
}

/**
 * Creator counter offer (POST /collabs/counter). On a pending invite it adds a counter (at most
 * MAX_COLLAB_COUNTERS); on a collab already `influencerCountered` it edits the latest one.
 * `reason` is required for a new counter. `goLiveConfirmed` is required on go-live campaigns.
 */
export interface CounterCollabRequestDto {
  collabId: string;
  /** Social collabs only; a UGC collab counters `proposedContent` instead. */
  deliverableCounts?: Record<string, number>;
  /** UGC collabs: the content terms the creator proposes. */
  proposedContent?: Record<string, ContentRequirement>;
  cash: number;
  credit: number;
  reason?: string | string[];
  note?: string;
  goLiveConfirmed?: GoLiveWindowDto;
}

/** How many counters a creator may send on one campaign offer. Enforced by the web backend. */
export const MAX_COLLAB_COUNTERS = 2;
